import React, { FC, Ref, useCallback } from 'react'
import { Select as AntSelect, Flex, Tooltip } from 'antd'
import type {
  SelectProps as AntSelectProps,
  SelectValue,
  RefSelectProps,
  OptionProps,
  LabeledValue
} from 'antd/es/select'
import * as Icons from '@ant-design/icons'

export type { LabeledValue }

export type SelectProps<T extends SelectValue> = AntSelectProps<T> & {
  onBottomReached?: () => void
  filter?: boolean
  instrumented?: boolean
  fieldName?: string
  lookupFunction?: () => void
  lookupTooltipText?: string
  style?: React.CSSProperties
}

function SelectInner<T extends SelectValue = SelectValue>(
  {
    children,
    onBottomReached,
    filter = true,
    suffixIcon,
    instrumented = false,
    fieldName = '',
    lookupFunction = undefined,
    lookupTooltipText = undefined,
    style = {
      width: '100%'
    },
    ...props
  }: SelectProps<T>,
  ref: Ref<RefSelectProps>
) {
  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      if (
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 20 &&
        typeof onBottomReached === 'function' &&
        !props.loading
      ) {
        onBottomReached()
      }
    },
    [onBottomReached, props.loading]
  )

  const handleFocus = e => {
    if (instrumented) {
      performance.mark('field-entered', {
        detail: { fieldName: `${fieldName}` }
      })
    }

    if (props.onFocus) {
      props.onFocus(e)
    }
  }

  const handleBlur = e => {
    if (instrumented) {
      performance.mark('field-exited', {
        detail: { fieldName: `${fieldName}` }
      })
      performance.measure('field-duration', {
        start: 'field-entered',
        end: 'field-exited',
        detail: { fieldName: `${fieldName}` }
      })
    }

    if (props.onBlur) {
      props.onBlur(e)
    }
  }

  return (
    <Flex align="center" gap="4px">
      <AntSelect
        style={style}
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: { maxWidth: '95vw' }
          }
        }}
        optionFilterProp="aria-label"
        {...props}
        filterOption={filter}
        onPopupScroll={handlePopupScroll}
        ref={ref}
        suffixIcon={props.loading ? <Icons.LoadingOutlined /> : suffixIcon}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </AntSelect>
      {lookupFunction && (
        <Tooltip title={lookupTooltipText}>
          <Icons.SearchOutlined onClick={lookupFunction} />
        </Tooltip>
      )}
    </Flex>
  )
}

const ForwardedSelect = React.forwardRef(SelectInner)

//
// Option

type OptionType = FC<OptionProps & { 'aria-label': string }> & {
  isSelectOption: boolean
}
const Option: OptionType = props => <AntSelect.Option {...props} />
Option.isSelectOption = true

//
// Exports

export const Select = ForwardedSelect as typeof ForwardedSelect & {
  OptGroup: typeof AntSelect.OptGroup
  Option: typeof Option
}

Select.OptGroup = AntSelect.OptGroup
Select.Option = Option
